import React, { useState } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Button,
} from '@mui/material';
import {
  BusinessOutlined as CompanyIcon,
  ChevronRightOutlined as ChevronRightIcon,
  PersonRemoveOutlined as EndIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { formatCompanyTypeLabel, formatDisplayDate } from '../../../utils/apiMappers';
import { csAdminApi } from '../../../services/api';
import EndAppointmentModal from '../../../components/directors/EndAppointmentModal';

const CompaniesTab = ({ companies, currentDirectorId, onRefresh }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [endTarget, setEndTarget] = useState(null);
  const [endAllOpen, setEndAllOpen] = useState(false);

  const activeCompanies = (companies || []).filter((c) => c.isActive !== false);
  const pastCompanies = (companies || []).filter((c) => c.isActive === false);

  const handleEndOne = async (payload) => {
    if (!endTarget) return;
    const directorId = endTarget.directorId || currentDirectorId;
    await csAdminApi.endDirectorAppointment(directorId, {
      company_id: endTarget.companyId,
      ...payload,
    });
    enqueueSnackbar(`Appointment ended at ${endTarget.companyName}`, { variant: 'success' });
    setEndTarget(null);
    if (onRefresh) await onRefresh();
  };

  const handleEndAll = async (payload) => {
    await csAdminApi.endAllDirectorAppointments(currentDirectorId, payload);
    enqueueSnackbar('All company appointments ended', { variant: 'success' });
    setEndAllOpen(false);
    if (onRefresh) await onRefresh();
  };

  const renderCompanyList = (companyList, { allowEnd = false } = {}) => {
    if (companyList.length === 0) return null;

    return (
      <List sx={{ p: 0 }}>
        {companyList.map((company, index) => {
          const isViewingRecord = currentDirectorId != null
            && String(company.directorId) === String(currentDirectorId);

          return (
            <React.Fragment key={`${company.companyId}-${company.directorId}`}>
              <ListItem
                sx={{
                  px: 0,
                  py: 2,
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '8px',
                    bgcolor: '#E3F2FD',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mr: 2,
                  }}
                >
                  <CompanyIcon sx={{ fontSize: 24, color: '#1976D2' }} />
                </Box>
                <ListItemText
                  primary={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => navigate(`/companies/${company.companyId}`)}
                      >
                        {company.companyName}
                      </Typography>
                      {company.companyType && (
                        <Chip
                          label={formatCompanyTypeLabel(company.companyType)}
                          size="small"
                          sx={{
                            bgcolor: '#F5F5F5',
                            color: '#616161',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        />
                      )}
                      {isViewingRecord && (
                        <Chip
                          label="THIS PROFILE"
                          size="small"
                          sx={{
                            bgcolor: ACTIVE_GREEN_BG,
                            color: ACTIVE_GREEN,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        />
                      )}
                      {!company.isActive && (
                        <Chip
                          label="ENDED"
                          size="small"
                          sx={{
                            bgcolor: '#F5F5F5',
                            color: '#757575',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        />
                      )}
                    </Box>
                  )}
                  secondary={(
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        {company.position || 'Director'}
                      </Typography>
                      {company.appointmentDate && (
                        <Typography variant="body2" color="textSecondary">
                          • Appointed: {formatDisplayDate(company.appointmentDate)}
                        </Typography>
                      )}
                      {company.endDate && (
                        <Typography variant="body2" color="textSecondary">
                          • Ended: {formatDisplayDate(company.endDate)}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {allowEnd && company.isActive !== false && (
                    <IconButton
                      size="small"
                      color="error"
                      title="End appointment at this company"
                      onClick={() => setEndTarget(company)}
                    >
                      <EndIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => navigate(`/companies/${company.companyId}`)}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </ListItem>
              {index < companyList.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  if (!companies || companies.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Company Appointments
        </Typography>
        <Box textAlign="center" py={4}>
          <CompanyIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            No company appointments on file
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6">
          Company Appointments ({companies.length})
        </Typography>
        {activeCompanies.length > 1 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<EndIcon />}
            onClick={() => setEndAllOpen(true)}
          >
            End all appointments
          </Button>
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
        Active ({activeCompanies.length})
      </Typography>
      {renderCompanyList(activeCompanies, { allowEnd: true })}

      {pastCompanies.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Past ({pastCompanies.length})
          </Typography>
          {renderCompanyList(pastCompanies)}
        </Box>
      )}

      <EndAppointmentModal
        open={Boolean(endTarget)}
        onClose={() => setEndTarget(null)}
        onSubmit={handleEndOne}
        companyName={endTarget?.companyName}
        title="End company appointment"
        description="Ends this board seat only. The director profile and pool record are kept for audit history."
      />

      <EndAppointmentModal
        open={endAllOpen}
        onClose={() => setEndAllOpen(false)}
        onSubmit={handleEndAll}
        title="End all company appointments"
        description="Ends every active board appointment for this director. Portal login is disabled automatically when no active appointments remain."
      />
    </Paper>
  );
};

export default CompaniesTab;
